
import React, { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { importFromCSV } from "@/utils/csvUtils";
import { toast } from "sonner";
import { Info, AlertTriangle, RefreshCw, FileText } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useNavigate } from "react-router-dom";
import { useCollections } from "@/contexts/collections";

interface ImportDataDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onImportSuccess?: () => void;
}

const ImportDataDialog = ({ open, onOpenChange, onImportSuccess }: ImportDataDialogProps) => {
  const [isImporting, setIsImporting] = useState(false);
  const [importComplete, setImportComplete] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [importStatus, setImportStatus] = useState<string | null>(null);
  const navigate = useNavigate();
  const { refreshCollections } = useCollections();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length === 1) {
      const file = e.target.files[0];
      if (file.type === "text/csv" || file.name.endsWith(".csv")) {
        setSelectedFile(file);
        setError(null);
        setImportStatus(null);
        setImportComplete(false);
      } else {
        setError("Please select a CSV file");
        setSelectedFile(null);
      }
    }
  };

  const validateSelectedFile = (): boolean => {
    if (!selectedFile) {
      setError("Please select a CSV file to import");
      return false;
    }
    return true;
  };

  const refreshPage = () => {
    // Reload the current page to show the newly imported data
    window.location.reload();
  };

  const handleImport = async () => {
    if (!validateSelectedFile()) {
      return;
    }

    setIsImporting(true);
    setError(null);
    setImportStatus("Starting import process...");
    
    try {
      // Add an artificial delay for better UX feedback
      setImportStatus("Parsing CSV file...");
      await new Promise(resolve => setTimeout(resolve, 500));
      
      setImportStatus("Importing data to database...");
      
      await importFromCSV(selectedFile);
      
      // Mark as complete but don't close dialog so user can see the refresh button
      setImportComplete(true);
      setSelectedFile(null);
      setImportStatus(null);
      
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      
      // Refresh collections data
      refreshCollections();
      
      // Call the success callback if provided
      if (onImportSuccess) {
        onImportSuccess();
      }
    } catch (error: any) {
      console.error("Import failed:", error);
      setError(error.message || "Import failed. Please try again.");
      toast.error("Import failed", {
        description: "Please check the error details in the import dialog"
      });
    } finally {
      setIsImporting(false);
      setImportStatus(null);
    }
  };

  const resetForm = () => {
    setSelectedFile(null);
    setError(null);
    setImportStatus(null);
    setImportComplete(false);
    
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <Dialog 
      open={open} 
      onOpenChange={(open) => {
        if (!open) resetForm();
        onOpenChange(open);
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Import Data</DialogTitle>
          <DialogDescription>
            Upload a CSV file to import your content, collections, tags, and boards.
          </DialogDescription>
        </DialogHeader>
        
        {importComplete ? (
          <div className="space-y-4 py-2">
            <Alert className="bg-green-50 border-green-200">
              <Info className="h-4 w-4 text-green-500" />
              <AlertDescription className="text-sm text-green-700">
                Import completed successfully! Your content, collections, tags, boards, and their relationships have been imported. You need to refresh the page to see your imported data.
              </AlertDescription>
            </Alert>
            
            <div className="flex justify-center">
              <Button 
                onClick={refreshPage} 
                className="gap-2"
              >
                <RefreshCw className="h-4 w-4" />
                Refresh Page
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4 py-2">
            <Alert className="bg-blue-50 border-blue-200">
              <Info className="h-4 w-4 text-blue-500" />
              <AlertDescription className="text-sm text-blue-700">
                Import data from a CSV file. The file should have columns for Title, URL, Description, Notes, ContentType, Collection, Tags, Boards, and Thumbnail.
              </AlertDescription>
            </Alert>
            
            <div className="grid w-full max-w-sm items-center gap-1.5 mt-4">
              <label 
                htmlFor="csv-upload" 
                className="cursor-pointer border-2 border-dashed border-gray-300 rounded-md p-4 text-center hover:bg-gray-50 transition-colors"
              >
                <FileText className="h-6 w-6 mx-auto mb-2 text-gray-400" />
                <span className="text-sm font-medium">
                  {selectedFile
                    ? `${selectedFile.name} selected`
                    : "Click to upload CSV file"}
                </span>
                <input
                  id="csv-upload"
                  type="file"
                  accept=".csv"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  className="hidden"
                />
              </label>
            </div>
            
            <Alert className="bg-blue-50 border-blue-200">
              <Info className="h-4 w-4 text-blue-500" />
              <AlertDescription className="text-sm text-blue-700">
                Collections, tags, and boards mentioned in the CSV that don't exist will be automatically created.
              </AlertDescription>
            </Alert>
            
            {error && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>Error</AlertTitle>
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            
            {importStatus && (
              <div className="text-sm text-blue-500 mt-2 flex items-center gap-2">
                <div className="h-4 w-4 rounded-full border-2 border-blue-500 border-t-transparent animate-spin"></div>
                <span>{importStatus}</span>
              </div>
            )}
          </div>
        )}
        
        <DialogFooter className="flex justify-between">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isImporting}
          >
            {importComplete ? "Close" : "Cancel"}
          </Button>
          
          {!importComplete && (
            <Button 
              onClick={handleImport}
              disabled={isImporting || !selectedFile}
            >
              {isImporting ? (
                <>
                  <span className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></span>
                  Importing...
                </>
              ) : (
                "Import"
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ImportDataDialog;
